import {
  useGetAllWaitingLinesFinancialDataQuery,
  useGetWaitingLineFinancialDataQuery,
  WaitingLineFinancialDataFragment,
} from 'lib/graphql/graphql';
import { mergePaginatedList, usePagination } from 'lib/graphql/pagination';
import useEvent from 'providers/event/useEvent';
import { useMemo } from 'react';

import useAggregateFilter from '../../helpers/useAggregateFilter';

interface QueryInfos {
  loading: boolean
  hasNextPage: boolean
  fetchMore: () => Promise<any>
}

function useFinancialDataQuery(selectedTime: string, selectedWaitingLine: string) {
  const { id: eventId } = useEvent();

  const filter = useAggregateFilter(selectedTime);

  const allQuery = usePagination(
    useGetAllWaitingLinesFinancialDataQuery({
      variables: { eventId, filter },
      skip: selectedWaitingLine !== 'all',
    }),
    (d) => d.getAllWaitingLinesFinancialData.lastEvaluated,
    (existing, incoming) => ({
      getAllWaitingLinesFinancialData: mergePaginatedList(
        existing.getAllWaitingLinesFinancialData,
        incoming.getAllWaitingLinesFinancialData,
        'items',
      ),
    }),
  );

  const query = useGetWaitingLineFinancialDataQuery({
    variables: { waitingLineId: selectedWaitingLine, filter },
    skip: selectedWaitingLine === 'all',
  });

  return useMemo<[WaitingLineFinancialDataFragment[], QueryInfos]>(() => {
    if (selectedWaitingLine !== 'all') {
      const item = query.data?.getWaitingLineFinancialData;
      return [item ? [item] : [], { ...query, hasNextPage: false, fetchMore: async () => {} }];
    }
    return [allQuery.data?.getAllWaitingLinesFinancialData.items || [], allQuery];
  }, [allQuery, query, selectedWaitingLine]);
}

export default useFinancialDataQuery;
